import { useState, FC, Fragment, useEffect } from 'react';
import {
  useMediaQuery,
  Button,
  Divider,
  List,
  ListItemText,
  Collapse,
  ListItemSecondaryAction,
  IconButton,
  ListItemButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ExpandMore, ExpandLess, Check, Add } from '@mui/icons-material';
import clsx from 'clsx';
import { IExclusionSet } from '../../../../../models';
import { ExcludedOptionItem } from './excluded-option-item';
import {
  createNeighborhoodSeriesSelectedExclusionOptions,
  removeNeighborhoodSeriesSelectedExclusionOptions
} from '../../../../../fetch';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { formatCapitalCase, getTextBeforeHypen } from '../../../../../helpers/format';

interface IExcludedOptionList {
  set: IExclusionSet;
  selections: string[];
  setSelections: number[];
  refetch: () => void;
}

export const ExcludedOptionList: FC<IExcludedOptionList> = ({
  set,
  selections,
  setSelections,
  refetch
}) => {
  const isUniversalOption = !!set.optionSetId;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { neighborhoodId, selectedSeriesId }: { neighborhoodId: string; selectedSeriesId: string } =
    useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [isExcluded, setIsExcluded] = useState(
    !isUniversalOption
      ? selections.includes(set.options?.[0].value)
      : setSelections.includes(isUniversalOption ? +set.optionSetId : null)
  );

  useEffect(() => {
    setIsExcluded(
      !isUniversalOption
        ? selections.includes(set.options?.[0].value)
        : setSelections.includes(isUniversalOption ? +set.optionSetId : null)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSelections, selections]);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const handleClick = async () => {
    if (isUniversalOption) {
      if (isExcluded) {
        await removeOption();
      } else {
        await createOption();
      }
    } else {
      if (isExcluded) {
        await removeItem();
      } else {
        await createItem();
      }
    }
  };

  const removeOption = async () => {
    try {
      await removeNeighborhoodSeriesSelectedExclusionOptions(neighborhoodId, selectedSeriesId, {
        optionSetId: isUniversalOption ? +set.optionSetId : null
      });
      enqueueSnackbar(`Successfully included ${set.className}!`, { variant: 'success' });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error including ${set.className}.`, { variant: 'error' });
    }
  };

  const createOption = async () => {
    try {
      await createNeighborhoodSeriesSelectedExclusionOptions(neighborhoodId, selectedSeriesId, {
        optionSetId: isUniversalOption ? +set.optionSetId : null
      });
      enqueueSnackbar(`Successfully Excluded ${set.className}`, { variant: 'success' });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error Excluding ${set.className}.`, { variant: 'error' });
    }
  };

  const removeItem = async () => {
    const option = set.options[0];
    const setId = isUniversalOption ? +set.optionSetId : null;

    try {
      await removeNeighborhoodSeriesSelectedExclusionOptions(neighborhoodId, selectedSeriesId, {
        optionName: isNaN(+option.value) ? (option.name ? option.name : option.value) : null,
        optionSetId: setId ?? null,
        sageID: setId ? null : option?.value
      });

      enqueueSnackbar(`Successfully included ${option.name ? option.name : option.value}`, {
        variant: 'success'
      });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error including ${option.name ? option.name : option.value}.`, {
        variant: 'error'
      });
    }
  };

  const createItem = async () => {
    const option = set.options[0];
    const setId = !!set.optionSetId ? +set.optionSetId : null;
    try {
      await createNeighborhoodSeriesSelectedExclusionOptions(neighborhoodId, selectedSeriesId, {
        optionName: isNaN(+option.value) ? (option.name ? option.name : option.value) : null,
        optionSetId: setId ?? null,
        sageID: setId ? null : option?.value
      });
      enqueueSnackbar(`Successfully Excluded ${option.name ? option.name : option.value}`, {
        variant: 'success'
      });
      refetch();
    } catch (error) {
      enqueueSnackbar(`Error Excluding ${option.name ? option.name : option.value}.`, {
        variant: 'error'
      });
    }
  };

  const makeCommaSeparatedArray = () => {
    // Use a set to retain only distinct values
    const listItemSet = set?.options?.reduce((acc, curr) => {
      acc.add(formatCapitalCase(getTextBeforeHypen(curr.value)));
      return acc;
    }, new Set<string>());
    // Return an array of distinct values
    return Array.from(listItemSet) ?? [];
  };

  return (
    <>
      <ListItemButton
        classes={{
          root: classes.listItemWithCats
        }}
        onClick={() => {
          if (!isUniversalOption) {
            return null;
          }
          return setOpen(!open);
        }}
      >
        {isUniversalOption && (
          <IconButton
            color="default"
            className={classes.openButton}
            aria-label="open"
            onClick={() => {
              if (!isUniversalOption) {
                return null;
              }
              return setOpen(!open);
            }}
            size="large"
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
        <ListItemText
          classes={{ root: classes.listItemText }}
          primary={
            isUniversalOption
              ? `${set.className}${
                  makeCommaSeparatedArray().length > 0 ? ' - ' : ''
                }${makeCommaSeparatedArray().slice(0, 3).join(', ')}${
                  makeCommaSeparatedArray().length > 3 ? ', ...' : ''
                }`
              : `${set.className} - ${formatCapitalCase(
                  set.options?.[0].name ? set.options?.[0].name : set.options?.[0].value
                )}`
          }
        />
        <ListItemSecondaryAction className={classes.buttonWrapper}>
          <Button
            color="primary"
            variant={isExcluded ? 'contained' : 'text'}
            startIcon={isExcluded ? <Check /> : <Add />}
            className={clsx(classes.editButton, classes.selected)}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleClick();
            }}
          >
            {isMobile ? '' : `${isExcluded ? 'Excluded' : 'Exclude'}`}
          </Button>
        </ListItemSecondaryAction>
      </ListItemButton>
      <Divider />
      <Collapse in={open} timeout="auto" unmountOnExit className={classes.nested}>
        <List component="div" disablePadding>
          {set.options &&
            set.options.map((option, optionIndex) => {
              return (
                <Fragment key={`${option.value}-${optionIndex}`}>
                  <ExcludedOptionItem
                    option={option}
                    setId={+set.optionSetId}
                    exclusions={selections}
                    refetch={refetch}
                  />
                </Fragment>
              );
            })}
        </List>
      </Collapse>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper
  },
  nested: {
    paddingLeft: theme.spacing(2)
  },
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  listItemWithCats: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1)
  },
  nestedListItemWithCats: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2)
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    marginRight: 8,
    padding: 0
  },
  openButton: {
    padding: 0
  },
  selected: {
    fontWeight: 'bold',
    padding: `2px 8px`,
    borderRadius: 5,
    marginRight: 0
  },
  listItemText: {
    maxWidth: 'calc(100% - 140px)'
  }
}));
